import React, { useEffect, useState } from "react";

const AuthShowcaseSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-advance showcase slider every 6 seconds unless hovered
  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === 0 ? 1 : 0));
    }, 6000);
    return () => clearInterval(timer);
  }, [isHovered]);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="lg:col-span-6 hidden lg:block sticky top-28 w-full h-full pb-0 select-none"
    >
      <div className="relative w-full">
        {/* Slide 1: AI Lab */}
        <div
          className={`transition-all duration-500 ease-in-out transform ${
            currentSlide === 0
              ? "opacity-100 translate-x-0 scale-100 pointer-events-auto relative"
              : "opacity-0 translate-x-8 scale-95 pointer-events-none absolute inset-0"
          }`}
        >
          <div className="relative w-full overflow-hidden rounded-[2.5rem] border border-gray-150/40 shadow-[0_12px_40px_rgba(0,0,0,0.03)] bg-white p-6 lg:p-8 xl:p-10 min-h-[460px] lg:min-h-[500px] xl:min-h-[540px] flex flex-col justify-center">
            {/* Background Image */}
            <img
              src="/img/reg/reg_slider_ailab.png"
              alt="AI Lab Background"
              className="absolute inset-0 w-full h-full object-cover object-right"
            />

            {/* Purple Gradient Glow on Top-Left Corner */}
            <div className="absolute top-0 left-0 w-[450px] h-[450px] bg-[radial-gradient(circle_at_0%_0%,rgba(168,85,247,0.22)_0%,rgba(168,85,247,0.08)_35%,rgba(168,85,247,0.02)_60%,transparent_100%)] pointer-events-none z-[2]" />

            {/* Content Overlay */}
            <div className="relative z-10 w-[62%] xl:w-[58%] 2xl:w-[56%] flex flex-col justify-center">
              {/* Blurry White Haze Background for Text Readability */}
              <div className="absolute -inset-10 bg-white/70 blur-3xl rounded-full pointer-events-none z-[-1]" />
              
              {/* Top Tag */}
              <span className="bg-[#F3E8FF] text-[#9333EA] text-[10px] font-black uppercase tracking-wider px-3.5 py-1.5 rounded-full w-fit mb-3.5 shadow-sm border border-[#E9D5FF]/30">
                PRACTIDO AI LAB
              </span>

              {/* Heading */}
              <h3 className="text-xl xl:text-3xl font-black text-[#080E38] tracking-tight leading-tight mb-2.5">
                Future Skills for Every Child
              </h3>

              {/* Subheading */}
              <p className="text-[10px] xl:text-sm text-gray-500 font-semibold leading-relaxed mb-4">
                Industry-relevant courses designed to ignite curiosity and build real-world skills.
              </p>

              {/* Skills pills grid (Image 2 style with white rounded cards and SVG icons) */}
              <div className="grid grid-cols-2 gap-x-2.5 gap-y-2.5 w-full">
                {[
                  {
                    text: "AI & Robotics",
                    bg: "bg-[#F3E8FF]",
                    textCol: "text-[#9333EA]",
                    icon: (
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 3h6M12 3v3m-6 3h12a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6a2 2 0 012-2zm4 4h.01M14 13h.01M9 17h6" />
                      </svg>
                    ),
                  },
                  {
                    text: "3D Printing & Modeling",
                    bg: "bg-[#DCFCE7]",
                    textCol: "text-[#15803D]",
                    icon: (
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-14L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    ),
                  },
                  {
                    text: "Coding",
                    bg: "bg-[#EFF6FF]",
                    textCol: "text-[#1D4ED8]",
                    icon: (
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l-3 3 3 3m8-6l3 3-3 3M13 5l-2 14" />
                      </svg>
                    ),
                  },
                  {
                    text: "Aerodynamics",
                    bg: "bg-[#EFF6FF]",
                    textCol: "text-[#1D4ED8]",
                    icon: (
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    ),
                  },
                  {
                    text: "Design",
                    bg: "bg-[#FDF2F8]",
                    textCol: "text-[#BE185D]",
                    icon: (
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    ),
                  },
                  {
                    text: "And more...",
                    bg: "bg-[#F3E8FF]",
                    textCol: "text-[#7E22CE]",
                    icon: (
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 10a2 2 0 110 4 2 2 0 010-4zm6 0a2 2 0 110 4 2 2 0 010-4zm6 0a2 2 0 110 4 2 2 0 010-4z" />
                      </svg>
                    ),
                  },
                  {
                    text: "Video Editing",
                    bg: "bg-[#FFEDD5]",
                    textCol: "text-[#C2410C]",
                    icon: (
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    ),
                  },
                ].map((skill, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-full p-1.5 pr-4 flex items-center gap-2.5 shadow-[0_2.5px_8px_rgba(0,0,0,0.035)] border-none hover:shadow-md transition-all duration-200"
                  >
                    <span className={`w-8 h-8 rounded-[12px] flex items-center justify-center flex-shrink-0 ${skill.bg} ${skill.textCol}`}>
                      {skill.icon}
                    </span>
                    <span className="truncate text-[#1E293B] font-bold text-[10px] xl:text-[11.5px] tracking-tight">
                      {skill.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Bottom Tag */}
              <div className="bg-[#EFF6FF] text-[#009DFF] px-3.5 py-2 rounded-xl text-[10px] font-black flex items-center gap-1.5 w-fit mt-6 shadow-sm border border-[#D5E6FF]/50">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 1.703 1 1 0 01-1.392-.174z" />
                </svg>
                For Classes 1 to 12
              </div>
            </div>
          </div>
        </div>

        {/* Slide 2: Olympiad App */}
        <div
          className={`transition-all duration-500 ease-in-out transform ${
            currentSlide === 1
              ? "opacity-100 translate-x-0 scale-100 pointer-events-auto relative"
              : "opacity-0 -translate-x-8 scale-95 pointer-events-none absolute inset-0"
          } flex flex-col gap-4`}
        >
          {/* Card 1: Olympiad App */}
          <div className="relative w-full overflow-hidden rounded-[2.5rem] border border-gray-150/40 shadow-[0_12px_40px_rgba(0,0,0,0.03)] bg-white p-6 lg:p-8 xl:p-10 min-h-[340px] lg:min-h-[380px] xl:min-h-[420px] flex flex-col justify-center">
            {/* Background Image */}
            <img
              src="/img/reg/reg_slider_app.png"
              alt="Olympiad App Background"
              className="absolute inset-0 w-full h-full object-cover object-right"
            />

            {/* Content Overlay */}
            <div className="relative z-10 w-[62%] xl:w-[58%] 2xl:w-[56%] flex flex-col justify-center">
              {/* Blurry White Haze Background for Text Readability */}
              <div className="absolute -inset-10 bg-white/75 blur-3xl rounded-full pointer-events-none z-[-1]" />
              
              {/* Top Tag */}
              <span className="bg-[#E0F2FE] text-[#0369A1] text-[10px] font-black uppercase tracking-wider px-3.5 py-1.5 rounded-full w-fit mb-3.5 shadow-sm border border-[#BAE6FD]/30">
                PRACTIDO OLYMPIAD APP
              </span>

              {/* Heading */}
              <h3 className="text-xl xl:text-3xl font-black text-[#080E38] tracking-tight leading-tight mb-2.5">
                Practice. Compete. Improve.
              </h3>

              {/* Subheading */}
              <p className="text-[10px] xl:text-sm text-gray-500 font-semibold leading-relaxed mb-4">
                Olympiad preparation made smart, fun and effective.
              </p>

              {/* Features list */}
              <div className="flex flex-col gap-2.5">
                {[
                  "Maths, Science, English & Computers Olympiad Preparation",
                  "Topic-wise Practice Tests",
                  "Leaderboards & Achievements",
                  "Parent Progress Dashboard",
                  "Performance Analytics",
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-2.5">
                    <span className="w-4 h-4 rounded-full bg-[#009DFF] flex items-center justify-center text-white flex-shrink-0 shadow-[0_2px_4px_rgba(0,157,255,0.3)]">
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-[10px] xl:text-sm font-bold text-gray-700 leading-tight">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Card 2: Horizontal Trust Card */}
          <div className="w-full bg-white rounded-[2rem] border border-gray-150/40 shadow-[0_12px_40px_rgba(0,0,0,0.03)] p-4 xl:p-5">
            <div className="grid grid-cols-2 2xl:grid-cols-4 gap-y-4 gap-x-3 xl:gap-x-4 w-full">
              {/* Item 1 */}
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-[#F3E8FF] flex items-center justify-center text-[#9333EA] flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] xl:text-[11px] text-gray-500 font-semibold leading-tight">Trusted by</span>
                  <span className="text-[11px] xl:text-[12px] font-black text-[#080E38] leading-tight">50,000+ Students</span>
                </div>
              </div>

              {/* Item 2 */}
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-[#DCFCE7] flex items-center justify-center text-[#15803D] flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] xl:text-[11px] text-gray-500 font-semibold leading-tight">Partnered with</span>
                  <span className="text-[11px] xl:text-[12px] font-black text-[#080E38] leading-tight">100+ Schools</span>
                </div>
              </div>

              {/* Item 3 */}
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-[#EFF6FF] flex items-center justify-center text-[#1D4ED8] flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] xl:text-[11px] text-gray-500 font-semibold leading-tight">Practice Bank</span>
                  <span className="text-[11px] xl:text-[12px] font-black text-[#080E38] leading-tight">10,000+ Questions</span>
                </div>
              </div>

              {/* Item 4 */}
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-[#FEF9C3] flex items-center justify-center text-[#A16207] flex-shrink-0">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] xl:text-[11px] text-gray-500 font-semibold leading-tight">App Rating</span>
                  <span className="text-[11px] xl:text-[12px] font-black text-[#080E38] leading-tight">4.8 / 5 Stars</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slider Pagination Controls */}
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            type="button"
            onClick={() => setCurrentSlide(0)}
            className={`h-2 rounded-full transition-all duration-300 ${
              currentSlide === 0 ? "w-8 bg-[#009DFF]" : "w-2 bg-gray-300 hover:bg-gray-400"
            }`}
            aria-label="Slide 1"
          />
          <button
            type="button"
            onClick={() => setCurrentSlide(1)}
            className={`h-2 rounded-full transition-all duration-300 ${
              currentSlide === 1 ? "w-8 bg-[#009DFF]" : "w-2 bg-gray-300 hover:bg-gray-400"
            }`}
            aria-label="Slide 2"
          />
        </div>
      </div>
    </div>
  );
};

export default AuthShowcaseSlider;
